import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Loader, Button, Card, Stack, Title } from '@mantine/core';
import { IconUserPlus } from '@tabler/icons-react';

import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { useAuth } from '../../context/AuthProvider';
import { showErrorNotification } from '../../notifications';
import Stores from './Stores';
import { OpsDivision } from '../../codegen/graphql';
import { StoreWithManagers } from '../../ts/interfaces/stores/store';
import { useUserPortal } from '../../context/UserPortalContext';

interface OperationsHierarchyData {
    getOperationsHierarchy: {
        opsAreas: Array<{
            divisions?: Array<OpsDivision> | null;
        }>;
    };
}

function MyTeam() {
    const { authUser } = useAuth();
    const { openUserSidesheet } = useUserPortal();
    const {
        data: operationsHierarchy,
        loading: loadingOperationsHierarchy,
        error: operationsHierarchyError,
    } = useQuery<OperationsHierarchyData>(GET_OPS_HIERARCHY, {
        variables: {
            includeClosedStores: false,
            filterEmptyHierarchies: true,
        },
    });

    const userDivision = useMemo(() => {
        const opsAreas = operationsHierarchy?.getOperationsHierarchy?.opsAreas;
        if (!opsAreas || !authUser) return null;

        return (
            opsAreas
                .flatMap(area => area.divisions || [])
                .find(div => div?.manager?.id === authUser.id) || null
        );
    }, [operationsHierarchy, authUser]);

    const stores = useMemo(() => {
        if (!userDivision?.stores) return [] as StoreWithManagers[];
        return userDivision.stores.map(
            (store): StoreWithManagers => ({
                ...store,
                managers: store.managers || null,
            }),
        );
    }, [userDivision]);

    const handleAddStoreManager = () => {
        if (!userDivision) {
            return;
        }
        openUserSidesheet();
    };

    if (operationsHierarchyError) {
        showErrorNotification('Error loading hierarchy data');
        return null;
    }

    if (loadingOperationsHierarchy) {
        return <Loader />;
    }

    if (!userDivision) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg">No division found for current user</p>
            </div>
        );
    }

    return (
        <Stack gap="md">
            <Card shadow="sm" p="lg">
                <div className="flex justify-between items-center">
                    <div>
                        <Title order={2} className="font-medium">
                            {userDivision.name}
                        </Title>
                    </div>
                    <Button
                        radius="xl"
                        leftSection={<IconUserPlus size={20} />}
                        onClick={handleAddStoreManager}
                        w="fit-content"
                        size="sm"
                    >
                        Create Store Manager
                    </Button>
                </div>
            </Card>
            <div>
                <Stores stores={stores} division={userDivision} />
            </div>
        </Stack>
    );
}

export default MyTeam;
